import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { checkInputErrorRequired, useCheckBox, useInput } from '@chilecompra/react-kit'
import { CheckBox, Input, Skeleton, Typography } from '@chilecompra/react-kit/components'

import ProtectByFeatures from '../../components/Security/ProtectByFeatures'
import { STATUS_ABILITY_PROVIDER_DICTIONARY } from '../../config/settings/constants'
import { useImperativeFormRef } from '../../modules/hooks/useFormRef'
import { checkInputErrorCustomValue } from '../../modules/utils/checkers'
import { formatDNI } from '../../modules/utils/formatters'
import { onCleanInfoProviderDirectDealStepTwo, onGetInfoProviderThunk } from './DirectDealStepTwo.actions'
import { GridContainer, GridItem, Link, SearchIconCustom, SearchSuffix } from './DirectDealStepTwo.styles'

/**
 * The DirectDealStepTwoProvider's container.
 */
const DirectDealStepTwoProvider = ({ formStepTwoRef, loading, onOpenDialog, onRedirect, provider, storage }) => {
  const dispatch = useDispatch()

  const [hasErrorFoundProvider, setHasErrorFoundProvider] = useState(false)
  const [hasErrorSearchProvider, setHasErrorSearchProvider] = useState(false)

  const {
    onChange: handleForeignSupplierChange,
    setValue: setForeignSupplierValue,
    value: foreignSupplierValue
  } = useCheckBox({
    changeCallback: () => {
      dispatch(onCleanInfoProviderDirectDealStepTwo())
      handleProviderDniReset()
      setHasErrorFoundProvider(false)
      setHasErrorSearchProvider(false)
      setProviderValue('')
    },
    initialValue: storage?.foreignSupplier || false
  })

  const {
    error: providerDniError,
    onChange: handleProviderDniChange,
    onError: handleProviderDniError,
    onReset: handleProviderDniReset,
    setValue: setProviderValue,
    value: providerDniValue
  } = useInput({
    changeCallback: () => {
      dispatch(onCleanInfoProviderDirectDealStepTwo())
      setHasErrorFoundProvider(false)
      setHasErrorSearchProvider(false)
    },
    errorCallbacks: [
      checkInputErrorRequired(),

      checkInputErrorCustomValue(
        !foreignSupplierValue && formStepTwoRef.current.provider?.value && hasErrorFoundProvider,
        'No se ha encontrado RUT del proveedor'
      ),

      checkInputErrorCustomValue(
        foreignSupplierValue && formStepTwoRef.current.provider?.value && hasErrorFoundProvider,
        'Proveedor sin inscripción vigente en Mercado Público.'
      ),

      checkInputErrorCustomValue(
        formStepTwoRef.current.provider?.value && hasErrorSearchProvider,
        'Recuerda consultar la habilidad de proveedor antes de publicar el trato directo.'
      )
    ],
    formatCallbacks: !foreignSupplierValue ? [formatDNI] : [],
    initialValue: storage?.providerDni || ''
  })

  const handlerSearchProvider = () => {
    if (!providerDniValue) return

    dispatch(
      onGetInfoProviderThunk({
        callback: () => {
          handleProviderDniReset()
          setHasErrorFoundProvider(false)
          setHasErrorSearchProvider(false)
          setProviderValue(providerDniValue)
        },
        callbackError: () => setHasErrorFoundProvider(true),
        isForeign: foreignSupplierValue,
        providerDni: providerDniValue
      })
    )
  }

  useImperativeFormRef(formStepTwoRef, () => {
    return {
      foreignSupplierValue,
      provider: {
        error: providerDniError,
        onError: () => setHasErrorSearchProvider(true),
        value: providerDniValue
      }
    }
  }, [foreignSupplierValue, providerDniError, providerDniValue])

  useEffect(() => {
    setForeignSupplierValue(storage?.foreignSupplier || false)
    setProviderValue(storage?.providerDni || '')
  }, [storage])

  useEffect(() => {
    if (!provider?.isEnabled && provider) onOpenDialog()
  }, [provider])

  useEffect(() => {
    if (hasErrorFoundProvider || hasErrorSearchProvider) handleProviderDniError()
  }, [hasErrorFoundProvider, hasErrorSearchProvider])

  return (
    <GridContainer margin="33px 0" spacing={2}>
      <GridItem md={2} xs={12}>
        <Typography fontWeight="bold" tabIndex="0" variant="body1">
          Identificación del proveedor
        </Typography>

        <Typography padding="8px 0 0" tabIndex="0" variant="body2">
          Consulta el estado de habilidad del proveedor con el que realizarás el Trato Directo.
        </Typography>
      </GridItem>

      <GridItem md={6} xs={12}>
        <GridContainer spacing={1}>
          <ProtectByFeatures rule={features => features['provider.foreign.step.two']}>
            <GridItem md={12} sm={12} xs={12}>
              <CheckBox
                accessibility={{ label: '¿El proveedor es extranjero?' }}
                checked={foreignSupplierValue}
                label={
                  <Typography variant="body2" margin="4px 0 0 -11px" tabIndex="0">
                    ¿El proveedor es extranjero?
                  </Typography>
                }
                margin="0 0 0 -3px"
                onChange={handleForeignSupplierChange}
              />
            </GridItem>
          </ProtectByFeatures>

          <GridItem xs={12}>
            <Input
              error={providerDniError}
              info={`Ingresa el ${foreignSupplierValue ? 'ID' : 'RUT'} y luego haz click en la lupa para consultar su estado de habilidad`}
              label={foreignSupplierValue ? 'ID Proveedor extranjero' : 'RUT Proveedor'}
              loading={loading}
              onChange={handleProviderDniChange}
              suffix={
                <SearchSuffix aria-label="Buscar RUT" onClick={handlerSearchProvider}>
                  <SearchIconCustom /> Consultar
                </SearchSuffix>
              }
              value={providerDniValue}
            />
          </GridItem>

          <GridItem xs={12} />

          <GridItem xs={12}>
            {loading && <Skeleton />}

            {!loading && provider && (
              <>
                <Typography fontWeight="bold" tabIndex="0" variant="body2">
                  Razón Social
                </Typography>

                <Typography
                  color="gray1"
                  fontWeight="regular"
                  lineHeight="16px"
                  padding="8px 0 0"
                  tabIndex="0"
                  variant="h4"
                >
                  {provider?.businessName}
                </Typography>
              </>
            )}
          </GridItem>

          <GridItem xs={12}>
            {loading && <Skeleton />}

            {!loading && provider && (
              <>
                <Typography fontWeight="bold" tabIndex="0" variant="body2">
                  Estado de habilidad
                </Typography>

                <Typography
                  color={
                    {
                      [STATUS_ABILITY_PROVIDER_DICTIONARY.ABILITY]: 'success',
                      [STATUS_ABILITY_PROVIDER_DICTIONARY.IN_REVIEW]: 'warning',
                      [STATUS_ABILITY_PROVIDER_DICTIONARY.NOT_ABILITY]: 'error',
                      [STATUS_ABILITY_PROVIDER_DICTIONARY.NOT_INFORMATION]: 'default'
                    }[provider?.status]
                  }
                  fontWeight="bold"
                  tabIndex="0"
                  variant="h4"
                >
                  {provider?.status}
                </Typography>

                <Typography
                  color="black1"
                  fontWeight="regular"
                  lineHeight="16px"
                  padding="8px 0 0"
                  tabIndex="0"
                  variant="body2"
                >
                  Revisa más información sobre este proveedor accediendo a su{' '}
                  <Link alt="Link Ficha Proveedor" onClick={onRedirect}>
                    ficha en Mercado Público
                  </Link>
                </Typography>
              </>
            )}
          </GridItem>
        </GridContainer>
      </GridItem>
    </GridContainer>
  )
}

DirectDealStepTwoProvider.defaultProps = {
  formStepTwoRef: {
    current: {}
  },
  loading: false,
  onOpenDialog: () => {},
  onRedirect: () => {},
  provider: {
    businessName: '',
    isEnabled: false,
    status: ''
  },
  storage: {
    foreignSupplier: false,
    providerDni: ''
  }
}

DirectDealStepTwoProvider.propTypes = {
  formStepTwoRef: PropTypes.shape({
    current: PropTypes.shape(Object)
  }),
  loading: PropTypes.bool,
  onOpenDialog: PropTypes.func,
  onRedirect: PropTypes.func,
  provider: PropTypes.shape({
    businessName: PropTypes.string,
    isEnabled: PropTypes.bool,
    status: PropTypes.string
  }),

  storage: PropTypes.shape({
    foreignSupplier: PropTypes.bool,
    providerDni: PropTypes.string
  })
}

export default DirectDealStepTwoProvider
